import React, { useEffect, useRef, useCallback } from 'react'
import { Room, RoomEvent, ParticipantEvent, DisconnectReason, setLogLevel, LogLevel } from 'livekit-client'

// Suppress LiveKit internal warnings (e.g., 'ignoring incoming byte stream...')
setLogLevel(LogLevel.error)

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_RETRIES = 5
const BASE_RETRY_DELAY_MS = 1000 // doubles on each attempt

// ─── Error type catalogue ─────────────────────────────────────────────────────
export const CONNECTION_ERROR = {
  MIC_DENIED: 'mic_denied',
  MIC_NOT_FOUND: 'mic_not_found',
  NO_INTERNET: 'no_internet',
  SERVER_UNREACHABLE: 'server_unreachable',
  MAX_RETRIES_EXCEEDED: 'max_retries_exceeded',
  TOKEN_INVALID: 'token_invalid',
  UNKNOWN: 'unknown'
}

/**
 * LiveKitRoomClient
 *
 * Handles the full lifecycle of a LiveKit audio-only room connection with:
 *   - Microphone permission detection (denied / not found)
 *   - Network offline/online awareness
 *   - Automatic reconnection with exponential backoff (up to MAX_RETRIES)
 *   - Structured error propagation via setConnectionError
 *   - Safe cleanup on unmount / user-initiated disconnect
 */
const LiveKitRoomClient = ({
  connectStatus,       // boolean – true = initiate/maintain connection
  serverUrl,
  token,
  setConnectedToLiveKit, // 'connecting' | 'reconnecting' | 'connected' | 'disconnected'
  setIsSpeaking,         // 'user' | 'ai' | null
  setCoachStatus,        // boolean – remote participant present?
  setConnectionError,    // { type: CONNECTION_ERROR, message: string } | null
  setReconnectAttempt,   // number
  coacherName,
  userName
}) => {
  const roomRef = useRef(null)
  const audioElementsRef = useRef(new Map())
  const audioContainerRef = useRef(null)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef(null)
  const isMountedRef = useRef(true)
  const userInitiatedDisconnectRef = useRef(false)
  const isConnectingRef = useRef(false)
  // Stable ref to connectToRoom so the online handler can call it
  const connectToRoomRef = useRef(null)

  // ─── Lifecycle mount/unmount guard ────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      // Null the ref so the 'online' window listener can't reconnect after unmount
      connectToRoomRef.current = null
    }
  }, [])

  // ─── Safe setState wrappers ───────────────────────────────────────────────
  const safeSet = fn => (...args) => {
    if (isMountedRef.current) fn(...args)
  }
  const safeSetConnected = safeSet(setConnectedToLiveKit)
  const safeSetIsSpeaking = safeSet(setIsSpeaking)
  const safeSetCoachStatus = safeSet(setCoachStatus)
  const safeSetConnectionError = safeSet(setConnectionError)
  const safeSetReconnectAttempt = safeSet(setReconnectAttempt)

  // ─── Ensure audio container exists in DOM ─────────────────────────────────
  const ensureAudioContainer = () => {
    if (!audioContainerRef.current) {
      let container = document.getElementById('livekit-audio-container')
      if (!container) {
        container = document.createElement('div')
        container.id = 'livekit-audio-container'
        container.style.display = 'none'
        document.body.appendChild(container)
      }
      audioContainerRef.current = container
    }
    return audioContainerRef.current
  }

  // ─── Re-derive speaking state from the room snapshot ────────────────────
  const updateSpeaking = useCallback((room) => {
    if (!isMountedRef.current || !room) return
    const remoteSpeaking = Array.from(room.remoteParticipants.values()).some(
      p => p.isSpeaking
    )
    const localSpeaking = room.localParticipant?.isSpeaking

    if (remoteSpeaking || localSpeaking) {
      safeSetConnected('connected')
    }

    if (localSpeaking) {
      safeSetIsSpeaking('user')
    } else if (remoteSpeaking) {
      safeSetIsSpeaking('ai')
    } else {
      safeSetIsSpeaking(null)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Attach remote audio track ────────────────────────────────────────────
  const attachAudioTrack = (track, participant) => {
    const container = ensureAudioContainer()
    const el = track.attach()
    el.setAttribute('data-livekit-audio', 'true')
    el.setAttribute('data-participant-id', participant.sid)
    el.autoplay = true
    el.controls = false
    el.volume = 1.0
    el.muted = false

    audioElementsRef.current.set(participant.sid, el)
    container.appendChild(el)

    // Recover from autoplay policy blocks
    el.play().catch(() => {
      const unlock = () => {
        el.play().catch(() => { })
        document.removeEventListener('click', unlock)
        document.removeEventListener('keydown', unlock)
      }
      document.addEventListener('click', unlock, { once: true })
      document.addEventListener('keydown', unlock, { once: true })
    })
  }

  // ─── Detach / remove a participant's audio element ────────────────────────
  const detachParticipantAudio = sid => {
    const el = audioElementsRef.current.get(sid)
    if (el) {
      try { el.pause() } catch (_) { }
      el.remove()
      audioElementsRef.current.delete(sid)
    }
  }

  // ─── Full teardown ────────────────────────────────────────────────────────
  const cleanup = (opts = {}) => {
    const { silent = false } = opts

    // Mark as user-initiated so no handler can trigger reconnection
    userInitiatedDisconnectRef.current = true

    // Cancel pending retry
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }

    // Remove all audio elements
    audioElementsRef.current.forEach((el) => {
      try { el.pause() } catch (_) { }
      el.remove()
    })
    audioElementsRef.current.clear()

    // Remove audio container
    const container = document.getElementById('livekit-audio-container')
    if (container) {
      container.remove()
      audioContainerRef.current = null
    }

    // Disconnect room
    const room = roomRef.current
    if (room) {
      // ⚠️  CRITICAL: Remove ALL event listeners BEFORE calling disconnect().
      // room.disconnect() fires RoomEvent.Disconnected synchronously, which
      // would call our handler → scheduleReconnect() → new retry timer that
      // survives cleanup. Removing listeners first severs this chain.
      room.removeAllListeners()

      try {
        // Explicitly stop every local mic track so the OS mic indicator
        // turns off immediately, regardless of SDK internal timing.
        if (room.localParticipant) {
          room.localParticipant.audioTrackPublications.forEach(pub => {
            try {
              if (pub.track) {
                pub.track.stop()
                if (pub.track.mediaStreamTrack) {
                  pub.track.mediaStreamTrack.stop()
                }
              }
            } catch (_) { }
          })
        }
        room.disconnect(true) // stopTracks = true
      } catch (_) { }
      roomRef.current = null
    }

    // Prevent any stale connectToRoom reference from being called
    connectToRoomRef.current = null
    isConnectingRef.current = false

    if (!silent) {
      safeSetCoachStatus(false)
      safeSetIsSpeaking(null)
      safeSetConnected('disconnected')
      safeSetReconnectAttempt(0)
    }
  }

  // ─── Schedule a reconnect with exponential backoff ────────────────────────
  const scheduleReconnect = () => {
    if (!isMountedRef.current) return
    if (userInitiatedDisconnectRef.current) return

    retryCountRef.current += 1

    if (retryCountRef.current > MAX_RETRIES) {
      safeSetConnected('disconnected')
      safeSetConnectionError({
        type: CONNECTION_ERROR.MAX_RETRIES_EXCEEDED,
        message: 'Could not reconnect. Please check your connection and try again.'
      })
      safeSetReconnectAttempt(0)
      return
    }

    const delay = BASE_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current - 1)
    safeSetConnected('reconnecting')
    safeSetReconnectAttempt(retryCountRef.current)

    retryTimerRef.current = setTimeout(() => {
      if (isMountedRef.current && !userInitiatedDisconnectRef.current) {
        connectToRoom()
      }
    }, delay)
  }

  // ─── Resolve a clean error object from a caught exception ─────────────────
  const classifyError = err => {
    const name = err?.name || ''
    const message = err?.message || ''

    // Microphone privacy / permission denied (macOS, Chrome, Firefox)
    if (
      name === 'NotAllowedError' ||
      name === 'PermissionDeniedError' ||
      message.toLowerCase().includes('permission denied') ||
      message.toLowerCase().includes('not allowed')
    ) {
      return {
        type: CONNECTION_ERROR.MIC_DENIED,
        message: 'Microphone access is blocked. Please enable it in your browser or system settings.'
      }
    }

    // No mic hardware / device not found
    if (
      name === 'NotFoundError' ||
      name === 'DevicesNotFoundError' ||
      message.toLowerCase().includes('not found') ||
      message.toLowerCase().includes('no device')
    ) {
      return {
        type: CONNECTION_ERROR.MIC_NOT_FOUND,
        message: 'No microphone found. Please connect one and try again.'
      }
    }

    // Network offline
    if (!navigator.onLine) {
      return {
        type: CONNECTION_ERROR.NO_INTERNET,
        message: 'You\'re offline. Check your internet connection and try again.'
      }
    }

    // Server unreachable
    if (
      name === 'NetworkError' ||
      message.toLowerCase().includes('websocket') ||
      message.toLowerCase().includes('failed to connect') ||
      message.toLowerCase().includes('server')
    ) {
      return {
        type: CONNECTION_ERROR.SERVER_UNREACHABLE,
        message: 'Cannot reach the server. Please check your connection and try again.'
      }
    }

    return {
      type: CONNECTION_ERROR.UNKNOWN,
      message: 'Something went wrong. Please try again.'
    }
  }

  // ─── Main connect routine ─────────────────────────────────────────────────
  const connectToRoom = async () => {
    if (!isMountedRef.current) return
    if (isConnectingRef.current) return
    if (!token || !serverUrl) return

    isConnectingRef.current = true
    safeSetConnectionError(null)

    if (retryCountRef.current === 0) {
      safeSetConnected('connecting')
    }

    // ── Microphone permission check ──────────────────────────────────────────
    let localStream
    try {
      localStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      isConnectingRef.current = false
      safeSetConnected('disconnected')
      safeSetConnectionError(classifyError(err))
      return
    }
    // Stop the probe stream; LiveKit will open its own
    localStream.getTracks().forEach(t => t.stop())

    if (userInitiatedDisconnectRef.current || !isMountedRef.current) return

    // ── Create Room ──────────────────────────────────────────────────────────
    ensureAudioContainer()

    const room = new Room({
      // Let LiveKit handle its own reconnect cycle too
      reconnectPolicy: { maxRetries: 0 } // we manage retries ourselves
    })
    roomRef.current = room

    // ── Room event handlers ──────────────────────────────────────────────────

    room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
      safeSetConnected('connected')
      if (track.kind === 'audio') {
        attachAudioTrack(track, participant)
        participant.on(ParticipantEvent.IsSpeakingChanged, () => updateSpeaking(room))
      }
    })

    room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
      if (track.kind === 'audio') {
        detachParticipantAudio(participant.sid)
        track.detach().forEach(el => el.remove())
      }
    })

    room.on(RoomEvent.ParticipantConnected, participant => {
      safeSetCoachStatus(true)
      participant.on(ParticipantEvent.IsSpeakingChanged, () => updateSpeaking(room))
    })

    room.on(RoomEvent.ParticipantDisconnected, participant => {
      const remaining = room.remoteParticipants.size
      if (remaining === 0) {
        safeSetCoachStatus(false)
        safeSetIsSpeaking(null)
      }
    })

    room.on(RoomEvent.Reconnecting, () => {
      // LiveKit-native reconnect (short network blip)
      safeSetConnected('reconnecting')
    })



    // room.on(RoomEvent.TranscriptionReceived, (transcription, participant) => {
    //   const sender = participant?.isLocal ? (userName || 'User') : (coacherName || 'Backend')
    //   if (Array.isArray(transcription)) {
    //     transcription.forEach(t => {
    //       if (t.text) {
    //         console.log(`[${sender}]:`, t.text)
    //       }
    //     })
    //   }
    // })

    room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
      try {
        const strData = new TextDecoder().decode(payload)
        const data = JSON.parse(strData)
        if (data) {
          console.log('Received source_references:', data)
        }
      } catch (err) {
        console.error('Error parsing DataReceived payload:', err)
      }
    })

    room.on(RoomEvent.Reconnected, () => {
      retryCountRef.current = 0
      safeSetReconnectAttempt(0)
      safeSetConnectionError(null)
      safeSetConnected('connected')
      // Re-register speaking listener — LiveKit may have rebuilt the localParticipant
      room.localParticipant.off(ParticipantEvent.IsSpeakingChanged)
      room.localParticipant.on(ParticipantEvent.IsSpeakingChanged, () => updateSpeaking(room))
      // Re-register for any remote participants still in the room
      room.remoteParticipants.forEach(p => {
        p.off(ParticipantEvent.IsSpeakingChanged)
        p.on(ParticipantEvent.IsSpeakingChanged, () => updateSpeaking(room))
      })
    })

    room.on(RoomEvent.Disconnected, reason => {
      isConnectingRef.current = false

      if (userInitiatedDisconnectRef.current) {
        // Clean intentional exit — do nothing further
        return
      }

      // Classify network-loss vs other reasons
      const isNetworkIssue =
        reason === DisconnectReason.UNKNOWN_REASON ||
        reason === DisconnectReason.SIGNAL_CLOSE ||
        reason === undefined

      const isTokenInvalid =
        reason === DisconnectReason.CLIENT_INITIATED ||
        reason === DisconnectReason.DUPLICATE_IDENTITY ||
        reason === DisconnectReason.SERVER_SHUTDOWN

      if (isTokenInvalid) {
        safeSetConnected('disconnected')
        safeSetConnectionError({
          type: CONNECTION_ERROR.TOKEN_INVALID,
          message: 'Session ended. Please start a new one.'
        })
        return
      }

      if (isNetworkIssue) {
        scheduleReconnect()
        return
      }

      // Generic unexpected disconnect — retry
      scheduleReconnect()
    })

    // Local speaking
    room.localParticipant.on(ParticipantEvent.IsSpeakingChanged, () => updateSpeaking(room))

    // ── Connect ──────────────────────────────────────────────────────────────
    try {
      await room.connect(serverUrl, token, { autoSubscribe: true })

      if (userInitiatedDisconnectRef.current || !isMountedRef.current) {
        room.disconnect(true)
        return
      }

      retryCountRef.current = 0
      safeSetReconnectAttempt(0)
      safeSetConnected('connected')

      // Hydrate existing participants (coach was already in room)
      if (room.remoteParticipants.size > 0) {
        safeSetCoachStatus(true)
        room.remoteParticipants.forEach(participant => {
          participant.audioTrackPublications.forEach(pub => {
            if (pub.track) attachAudioTrack(pub.track, participant)
          })
          participant.on(ParticipantEvent.IsSpeakingChanged, () => updateSpeaking(room))
        })
      }

      // Publish microphone
      await room.localParticipant.setMicrophoneEnabled(true)

      isConnectingRef.current = false
    } catch (err) {
      isConnectingRef.current = false
      roomRef.current = null

      if (userInitiatedDisconnectRef.current) return

      const classified = classifyError(err)

      // Mic errors are not retryable — surface immediately
      if (
        classified.type === CONNECTION_ERROR.MIC_DENIED ||
        classified.type === CONNECTION_ERROR.MIC_NOT_FOUND
      ) {
        safeSetConnected('disconnected')
        safeSetConnectionError(classified)
        return
      }

      // Everything else — retry with backoff
      safeSetConnectionError(classified)
      scheduleReconnect()
    }
  }

  // ─── Network online/offline listeners ─────────────────────────────────────
  useEffect(() => {
    const handleOffline = () => {
      if (!isMountedRef.current) return
      safeSetConnectionError({
        type: CONNECTION_ERROR.NO_INTERNET,
        message: 'You\'re offline. Check your internet connection.'
      })
      // Downstream room will disconnect and trigger scheduleReconnect,
      // but update the UI now so the user is never left wondering.
      if (roomRef.current) {
        safeSetConnected('reconnecting')
      }
    }

    const handleOnline = () => {
      if (!isMountedRef.current || !connectToRoomRef.current) return
      if (userInitiatedDisconnectRef.current) return
      safeSetConnectionError(null)
      // Clear any pending retry timer and kick a fresh attempt immediately
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      retryCountRef.current = 0
      safeSetReconnectAttempt(0)
      connectToRoomRef.current()
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Main connection effect ───────────────────────────────────────────────
  useEffect(() => {
    // Keep the ref current so the online handler always calls the latest version
    connectToRoomRef.current = connectToRoom

    if (connectStatus) {
      userInitiatedDisconnectRef.current = false
      retryCountRef.current = 0
      connectToRoom()
    } else {
      userInitiatedDisconnectRef.current = true
      // Only show 'disconnected' if there was an actual live room.
      // Token resets with no active room must be silent to avoid
      // the 'disconnected' flash that kills the 'connecting' state.
      const hadRoom = !!roomRef.current
      cleanup({ silent: !hadRoom })
    }

    return () => {
      userInitiatedDisconnectRef.current = true
      cleanup({ silent: true })
    }
  }, [connectStatus, token]) // eslint-disable-line react-hooks/exhaustive-deps

  // This component renders nothing — purely handles the audio session logic.
  return null
}

export default LiveKitRoomClient
